import { useEffect, useContext } from "react";
import { useReactFlow, useUpdateNodeInternals } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { createNodeRefs } from "./flow/createNodeRefs";
import { StoreContext } from "../store/Store";
import { Controls } from "./layout/Controls";
import { getLayout } from "../api/s3";
import { Style } from "./Style";
import { actionTypes } from "../store/storeTypes";
import { getConfigs } from "../api/s3";
import { GlobalRefContext } from "../store/GlobalRefs";
import { Popup } from "./Popup";
import { getConfigStyle } from "./flow/getConfigStyle";
import { SelectCountry } from "../components/SelectCountry";
import { TabButtons } from "../components/TabButtons";
import { Menu } from "./Menu";
import { Audit } from "./Audit";
import { FlowChart } from "./FlowChart";

const debug = false;

const maxZoomOnFocus = 1.2;

export const Layout = () => {
  const { fitView } = useReactFlow();
  const [store, dispatch] = useContext(StoreContext);
  const grefs = useContext(GlobalRefContext);
  const {
    auth,
    countryData,
    edges,
    flowConfig,
    flowLayout,
    focusId,
    menuSelection,
    mode,
    newFlowLayoutTrigger,
    nodes,
    reset,
    selectedCountry,
    showHandles,
  } = store;

  const updateNodeInternals = useUpdateNodeInternals();

  //Create position and handle ref objects
  useEffect(() => {
    if (flowLayout) {
      if (debug) {
        console.log("Create position and handle ref objects", flowLayout);
      }
      const handles = flowLayout.properties.reduce((a, b) => {
        return { ...a, [b.id]: b.handles };
      }, {});
      grefs.positionRef.current = flowLayout.properties;
      grefs.handleRef.current = handles;
      dispatch({ type: actionTypes.SET_NEWFLOWLAYOUTTRIGGER, payload: true });
    }
  }, [dispatch, flowLayout, grefs.positionRef, grefs.handleRef]);
  //monitor nodechanges and update position ref
  useEffect(() => {
    if (nodes) {
      if (debug) {
        console.log("monitor nodechanges and update position ref", nodes);
      }
      const newNodes = JSON.parse(JSON.stringify(nodes));
      grefs.positionRef.current = newNodes;
    }
  }, [nodes, grefs.positionRef, grefs.dragNode, updateNodeInternals]);
  //get data and config and layout
  useEffect(() => {
    const asyncfunction = async () => {
      if (selectedCountry && menuSelection) {
        if (debug) {
          console.log(
            "get data and config and layout",
            menuSelection,
            selectedCountry
          );
        }
        const id = `${selectedCountry}_${menuSelection}`;
        grefs.lastLayoutIdRef.current = grefs.layoutIdRef.current;
        grefs.layoutIdRef.current = id;

        const defLayout = await getLayout(auth, id);
        if (defLayout && "Item" in defLayout) {
          dispatch({
            type: actionTypes.SET_VERSION,
            payload: defLayout["Item"].source,
          });
          const handles = defLayout["Item"].properties.reduce((a, b) => {
            return { ...a, [b.id]: b.handles };
          }, {});

          grefs.handleRef.current = handles;

          dispatch({
            type: actionTypes.SET_FLOWLAYOUT,
            payload: defLayout["Item"],
          });
        } else {
          dispatch({
            type: actionTypes.SET_VERSION,
            payload: "default",
          });
          grefs.handleRef.current = [{}];

          const initial = {
            id: selectedCountry,
            version: "default",
            properties: [],
            source: "default",
          };

          dispatch({
            type: actionTypes.SET_FLOWLAYOUT,
            payload: initial,
          });
        }

        const defConfig = await getConfigs(auth, "D2Legal");
        if ("Item" in defConfig) {
          dispatch({
            type: actionTypes.SET_FLOWCONFIG,
            payload: defConfig["Item"],
          });
        }
      }
    };
    if (auth) {
      asyncfunction();
    }
  }, [
    dispatch,
    auth,
    menuSelection,
    // menuSelectionTrigger,
    selectedCountry,
    grefs.handleRef,
    grefs.layoutIdRef,
    grefs.lastLayoutIdRef,
  ]);
  //create the node and edge collections
  useEffect(() => {
    if (
      flowConfig &&
      grefs.positionRef.current &&
      countryData &&
      newFlowLayoutTrigger
    ) {
      if (debug) {
        console.log("create the node and edge collections");
      }
      const config = getConfigStyle(flowConfig, mode);
      
      createNodeRefs(countryData, config, reset, mode, grefs);

      if (
        !nodes.map((node) => node.id).includes(grefs.startRef.current) ||
        mode === "layout"
      ) {
        let startNode = grefs.nodeCollectionRef.current.find(
          (node) => node.id === grefs.startRef.current
        );
        const placeholder = grefs.nodeCollectionRef.current.find(
          (node) =>
            node.data.layoutId === grefs.lastLayoutIdRef.current &&
            node.type === "PlaceholderNode"
        );

        const currentOffset = grefs.nodeOffsetsRef.current;

        //save offset for select type node
        if (grefs.selectPersistedRef.current) {
          if (
            grefs.selectPersistedRef.current in
            grefs.nodeOffsetsHistoryRef.current
          ) {
            //
          } else {
            grefs.nodeOffsetsHistoryRef.current[
              grefs.selectPersistedRef.current
            ] = currentOffset;
          }
        }

        if (placeholder) {
          //save placeholder positions
          grefs.placeholderPositions.current[grefs.selectPersistedRef.current] =
            placeholder.position;

          grefs.nodeOffsetsRef.current = {
            x: placeholder.position.x - startNode.position.x + currentOffset.x,
            y: placeholder.position.y - startNode.position.y + currentOffset.y,
          };

          const startPos = {
            x: placeholder.position.x + currentOffset.x,
            y: placeholder.position.y + currentOffset.y,
          };

          startNode = {
            ...startNode,
            position: startPos,
          };
          grefs.calculatedPositionsRef.current[startNode.id] = startPos;
        } else {
          //get saved placeholder position for select node
          const ph =
            grefs.placeholderPositions.current[
              grefs.selectPersistedRef.current
            ];
          const co =
            grefs.nodeOffsetsHistoryRef.current[
              grefs.selectPersistedRef.current
            ];            

          if (ph && co) {
            grefs.nodeOffsetsRef.current = {
              x: ph.x - startNode.position.x + co.x,
              y: ph.y - startNode.position.y + co.y,
            };

            const startPos = {
              x: ph.x + co.x,
              y: ph.y + co.y,
            };

            startNode = {
              ...startNode,
              position: startPos,
            };

          }
        }

        //calculate positions
        grefs.nodeCollectionRef.current.forEach((item) => {
          if (item.id in grefs.calculatedPositionsRef.current) {
            //do nothing
          } else {
            grefs.calculatedPositionsRef.current[item.id] = {
              x: item.position.x + grefs.nodeOffsetsRef.current.x,
              y: item.position.y + grefs.nodeOffsetsRef.current.y,
            };
          }
        });

        const newActiveNodes = nodes.concat([startNode]);

        if (mode === "flow") {
          grefs.focusRef.current = startNode.id;
        }

        dispatch({
          type: actionTypes.SET_NODES,
          payload:
            mode === "flow" ? newActiveNodes : grefs.nodeCollectionRef.current,
        });
        dispatch({
          type: actionTypes.SET_EDGES,
          payload: grefs.edgeCollectionRef.current,
        });
      }
      if (reset) {
        dispatch({
          type: actionTypes.SET_VERSION,
          payload: "Default",
        });
        dispatch({ type: actionTypes.SET_RESET, payload: false });
      }
      dispatch({ type: actionTypes.SET_NEWFLOWLAYOUTTRIGGER, payload: false });
    }
  }, [
    countryData,
    dispatch,
    flowConfig,
    grefs.handleRef,
    grefs.positionRef,
    mode,
    newFlowLayoutTrigger,
    reset,
    showHandles,
    grefs,
    nodes,
  ]);

  useEffect(() => {
    if (focusId) {
      if (debug) {
        console.log("focus");
      }
      fitView({
        nodes: [{ id: focusId }],
        duration: 500,
        maxZoom: maxZoomOnFocus,
      });
      dispatch({ type: actionTypes.SET_FOCUSID, payload: null });
    }
  }, [dispatch, focusId, fitView]);

  return nodes && edges && auth && mode ? (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        backgroundColor: flowConfig.layoutBackgroundColor,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Menu />
      <TabButtons />
      <Controls />
      <Popup />
      <SelectCountry />
      <Audit />
      <FlowChart />
      <Style />
    </div>
  ) : null;
};
